import { Button } from '@/components/ui/button';
import { FormField } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useFormsTranslation } from '@/hooks/useTranslation';
import { Info } from 'lucide-react';
import { memo } from 'react';
import type { Control } from 'react-hook-form';
import { FormFieldWrapper } from '../../../FormFieldWrapper';
import type { IdentificationFormData } from '../types';

/**
 * Props for the AdditionalDetailsSection component
 *
 * @property control - React Hook Form control object
 * @property showFinancialIntermediaryFields - Whether to show financial intermediary fields
 * @property showCorporateFields - Whether to show corporate-specific fields
 * @property showListedCompanyField - Whether to show listed company field
 * @property showListedCompanyFields - Whether to show listed company details
 * @property showParentSubsidiaryFields - Whether to show parent/subsidiary fields
 * @property financialIntermediaryTypes - Available financial intermediary type options
 * @property financialIntermediaryType - Currently selected financial intermediary type
 */
interface AdditionalDetailsSectionProps {
  control: Control<IdentificationFormData>;
  showFinancialIntermediaryFields: boolean;
  showCorporateFields: boolean;
  showListedCompanyField: boolean;
  showListedCompanyFields: boolean;
  showParentSubsidiaryFields: boolean;
  financialIntermediaryTypes: Array<{ value: string; label: string }>;
  financialIntermediaryType?: string;
}

/**
 * AdditionalDetailsSection Component
 *
 * Renders additional details section of the KYC Identification Form including:
 * - Financial intermediary status and details
 * - Listed company information
 * - Parent/subsidiary company relationships
 *
 * The section uses conditional rendering based on legal form and user selections
 * to show relevant fields dynamically. Component is memoized to prevent
 * unnecessary re-renders when props don't change.
 *
 * @param props - Component props
 * @returns Rendered additional details section
 */
const AdditionalDetailsSectionComponent = ({
  control,
  showFinancialIntermediaryFields,
  showCorporateFields,
  showListedCompanyField,
  showListedCompanyFields,
  showParentSubsidiaryFields,
  financialIntermediaryTypes,
  financialIntermediaryType,
}: AdditionalDetailsSectionProps) => {
  const { t: tForms } = useFormsTranslation();

  return (
    <div className='space-y-6'>
      <div className='flex items-center gap-2 mb-4'>
        <h4 className='text-md font-medium'>
          {tForms('identification.sections.additionalDetails')}
        </h4>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type='button'
                variant='ghost'
                size='sm'
                className='p-1 h-6 w-6 text-muted-foreground hover:text-foreground'
                aria-label={tForms(
                  'identification.ariaLabels.informationAbout',
                  {
                    section: tForms(
                      'identification.sections.additionalDetails'
                    ),
                  }
                )}
              >
                <Info className='h-4 w-4' />
              </Button>
            </TooltipTrigger>
            <TooltipContent side='top' className='max-w-xs'>
              <p className='text-sm'>
                {tForms('identification.tooltips.additionalDetailsNote')}
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      {/* Row 1: Financial Intermediary */}
      <div className='grid grid-cols-1 md:grid-cols-2 gap-4 items-start'>
        <FormField
          control={control}
          name='isFinancialIntermediary'
          render={({ field, fieldState }) => (
            <FormFieldWrapper
              label={tForms('identification.fields.isFinancialIntermediary')}
              name='isFinancialIntermediary'
              required
              tooltip={tForms(
                'identification.tooltips.isFinancialIntermediary'
              )}
              error={fieldState.error?.message}
            >
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger className='min-h-[44px] w-full'>
                  <SelectValue
                    placeholder={tForms(
                      'identification.placeholders.selectOption'
                    )}
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='yes'>
                    {tForms('identification.labels.yes')}
                  </SelectItem>
                  <SelectItem value='no'>
                    {tForms('identification.labels.no')}
                  </SelectItem>
                </SelectContent>
              </Select>
            </FormFieldWrapper>
          )}
        />
        {/* Empty space for alignment */}
        <div></div>
      </div>

      {/* Conditional Fields for Financial Intermediary */}
      {showFinancialIntermediaryFields && (
        <>
          <div className='grid grid-cols-1 md:grid-cols-2 gap-4 items-start'>
            <FormField
              control={control}
              name='financialIntermediaryType'
              render={({ field, fieldState }) => (
                <FormFieldWrapper
                  label={tForms(
                    'identification.fields.financialIntermediaryType'
                  )}
                  name='financialIntermediaryType'
                  required
                  tooltip={tForms(
                    'identification.tooltips.financialIntermediaryType'
                  )}
                  error={fieldState.error?.message}
                >
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger className='min-h-[44px] w-full'>
                      <SelectValue
                        placeholder={tForms(
                          'identification.placeholders.selectType'
                        )}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {financialIntermediaryTypes.map(type => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormFieldWrapper>
              )}
            />

            <FormField
              control={control}
              name='supervisoryOrganization'
              render={({ field, fieldState }) => (
                <FormFieldWrapper
                  label={tForms(
                    'identification.fields.supervisoryOrganization'
                  )}
                  name='supervisoryOrganization'
                  required
                  tooltip={tForms(
                    'identification.tooltips.supervisoryOrganization'
                  )}
                  error={fieldState.error?.message}
                >
                  <Input
                    {...field}
                    placeholder={tForms(
                      'identification.placeholders.supervisoryOrganization'
                    )}
                    className='min-h-[44px]'
                  />
                </FormFieldWrapper>
              )}
            />
          </div>

          {/* Conditional Text Field for 'Other' Financial Intermediary Type */}
          {financialIntermediaryType === 'other' && (
            <div className='grid grid-cols-1 md:grid-cols-2 gap-4 items-start'>
              <FormField
                control={control}
                name='financialIntermediaryTypeOther'
                render={({ field, fieldState }) => (
                  <FormFieldWrapper
                    label={tForms(
                      'identification.fields.financialIntermediaryTypeOther'
                    )}
                    name='financialIntermediaryTypeOther'
                    required
                    tooltip={tForms(
                      'identification.tooltips.financialIntermediaryTypeOther'
                    )}
                    error={fieldState.error?.message}
                  >
                    <Input
                      {...field}
                      placeholder={tForms(
                        'identification.placeholders.financialIntermediaryTypeOther'
                      )}
                      className='min-h-[44px]'
                    />
                  </FormFieldWrapper>
                )}
              />
              {/* Empty space for alignment */}
              <div></div>
            </div>
          )}
        </>
      )}

      {/* Corporate fields - only for specific legal forms */}
      {showCorporateFields && (
        <>
          <div className='grid grid-cols-1 md:grid-cols-2 gap-4 items-start'>
            {/* Listed Company field - hidden for GmbH */}
            {showListedCompanyField && (
              <FormField
                control={control}
                name='listedCompany'
                render={({ field, fieldState }) => (
                  <FormFieldWrapper
                    label={tForms('identification.fields.listedCompany')}
                    name='listedCompany'
                    required
                    tooltip={tForms('identification.tooltips.listedCompany')}
                    error={fieldState.error?.message}
                  >
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger className='min-h-[44px] w-full'>
                        <SelectValue
                          placeholder={tForms(
                            'identification.placeholders.selectOption'
                          )}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value='yes'>
                          {tForms('identification.labels.yes')}
                        </SelectItem>
                        <SelectItem value='no'>
                          {tForms('identification.labels.no')}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </FormFieldWrapper>
                )}
              />
            )}

            <FormField
              control={control}
              name='subsidiaryOwnership'
              render={({ field, fieldState }) => (
                <FormFieldWrapper
                  label={tForms('identification.fields.subsidiaryOwnership')}
                  name='subsidiaryOwnership'
                  required
                  tooltip={tForms(
                    'identification.tooltips.subsidiaryOwnership'
                  )}
                  error={fieldState.error?.message}
                >
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger className='min-h-[44px] w-full'>
                      <SelectValue
                        placeholder={tForms(
                          'identification.placeholders.selectOption'
                        )}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='yes'>
                        {tForms('identification.labels.yes')}
                      </SelectItem>
                      <SelectItem value='no'>
                        {tForms('identification.labels.no')}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </FormFieldWrapper>
              )}
            />
          </div>

          {/* Listed Company Details */}
          {showListedCompanyFields && (
            <div className='grid grid-cols-1 md:grid-cols-2 gap-4 items-start'>
              <FormField
                control={control}
                name='stockExchange'
                render={({ field, fieldState }) => (
                  <FormFieldWrapper
                    label={tForms('identification.fields.stockExchange')}
                    name='stockExchange'
                    required
                    tooltip={tForms('identification.tooltips.stockExchange')}
                    error={fieldState.error?.message}
                  >
                    <Input
                      {...field}
                      placeholder={tForms(
                        'identification.placeholders.stockExchange'
                      )}
                      className='min-h-[44px]'
                    />
                  </FormFieldWrapper>
                )}
              />

              <FormField
                control={control}
                name='tickerSymbol'
                render={({ field, fieldState }) => (
                  <FormFieldWrapper
                    label={tForms('identification.fields.tickerSymbol')}
                    name='tickerSymbol'
                    required
                    tooltip={tForms('identification.tooltips.tickerSymbol')}
                    error={fieldState.error?.message}
                  >
                    <Input
                      {...field}
                      placeholder={tForms(
                        'identification.placeholders.tickerSymbol'
                      )}
                      className='min-h-[44px]'
                    />
                  </FormFieldWrapper>
                )}
              />
            </div>
          )}

          {/* Parent Company Details */}
          {showParentSubsidiaryFields && (
            <div className='grid grid-cols-1 md:grid-cols-2 gap-4 items-start'>
              <FormField
                control={control}
                name='parentCompanyExchange'
                render={({ field, fieldState }) => (
                  <FormFieldWrapper
                    label={tForms(
                      'identification.fields.parentCompanyExchange'
                    )}
                    name='parentCompanyExchange'
                    required
                    tooltip={tForms(
                      'identification.tooltips.parentCompanyExchange'
                    )}
                    error={fieldState.error?.message}
                  >
                    <Input
                      {...field}
                      placeholder={tForms(
                        'identification.placeholders.parentCompanyExchange'
                      )}
                      className='min-h-[44px]'
                    />
                  </FormFieldWrapper>
                )}
              />

              <FormField
                control={control}
                name='parentTicker'
                render={({ field, fieldState }) => (
                  <FormFieldWrapper
                    label={tForms('identification.fields.parentTicker')}
                    name='parentTicker'
                    required
                    tooltip={tForms('identification.tooltips.parentTicker')}
                    error={fieldState.error?.message}
                  >
                    <Input
                      {...field}
                      placeholder={tForms(
                        'identification.placeholders.parentTicker'
                      )}
                      className='min-h-[44px]'
                    />
                  </FormFieldWrapper>
                )}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

// Memoize component to prevent re-renders when conditional visibility doesn't change
export const AdditionalDetailsSection = memo(AdditionalDetailsSectionComponent);
